'use client';

import { useState, useEffect } from 'react';

import Image from 'next/image';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import {
  BubbleAutopilot,
  BubbleInbound,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import { TemplatesList } from '@/components/sections/templates-list';
import Testimonials from '@/components/sections/testimonials';
import { MagicalText } from '@/components/ui/magical-text';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

const CONTENT = {
  en: {
    eyebrow: 'Helpdesks',
    heroTitleA: 'Use Open directly in',
    heroTitleB: 'helpdesk',
    heroSubtitleA: 'No migration. Live in under an hour.',
    heroSubtitleB: 'Real resolutions — not Freddy’s suggestions.',
    inboundTitle: 'Order tracking · #6231',
    inboundPreview: 'Where is my order #4521? It said delivered yesterday.',
    autopilotTitle: 'Autopilot · ticket #6231',
    autopilotSteps: [
      'Read ticket in Freshdesk',
      'Pulled tracking from carrier',
      'Left at safe place · photo confirmed',
      'Replied in Freshdesk thread',
      'Closed ticket · CSAT 5★',
    ],
    quoteIntroA: "Hey, I'm Mo, founder of Open.",
    quoteIntroB: "Don't take my word for it—companies like",
    automationWithOpen: 'automation with Open',
    quoteMid:
      'chose Open over Freddy AI. They did it because Open works better for them.',
    quoteEnd: "They're obsessed with their customers and don't compromise.",
    role: 'Founder & CEO, Open',
    moPhotoAlt: 'Mo, Founder of Open',
    vs: 'vs',
  },
  ar: {
    eyebrow: 'أنظمة الدعم',
    heroTitleA: 'استخدم Open داخل نظام دعم',
    heroTitleB: 'اللي عندك',
    heroSubtitleA: 'بدون ترحيل. جاهز خلال أقل من ساعة.',
    heroSubtitleB: 'حلول تقفل التذكرة — مو مجرد اقتراحات من Freddy.',
    inboundTitle: 'تتبّع الطلب · #6231',
    inboundPreview: 'وين طلبي #4521؟ مكتوب إنه تسلّم أمس.',
    autopilotTitle: 'Autopilot · تذكرة #6231',
    autopilotSteps: [
      'قرأ التذكرة في Freshdesk',
      'سحب التتبّع من شركة الشحن',
      'وجده متروكاً في مكان آمن · مع صورة',
      'ردّ في محادثة Freshdesk',
      'أقفل التذكرة · CSAT 5★',
    ],
    quoteIntroA: 'هلا، أنا Mo، مؤسس Open.',
    quoteIntroB: 'مو لازم تصدّقني — شركات مثل',
    automationWithOpen: 'أتمتة مع Open',
    quoteMid: 'اختاروا Open بدل Freddy AI. ليش؟ لأنه يشتغل لهم أحسن.',
    quoteEnd: 'مهووسين بعملائهم، وما يقبلون أنصاف الحلول.',
    role: 'المؤسس والرئيس التنفيذي، Open',
    moPhotoAlt: 'Mo، مؤسس Open',
    vs: 'مقابل',
  },
} as const;

// Companies that switched from Freshdesk (with descriptions)
const switchedCompanies = [
  {
    name: 'DeOnlineDrogist',
    logo: '/logos/logo-deonlinedrogist.svg',
    description: 'One of the largest online pharmacies in the Netherlands',
    automationRate: '73%',
  },
  {
    name: 'CookingLife',
    logo: '/logos/logo-cookinglife.svg',
    description:
      'Served over 1 million customers with 10,000+ curated kitchen products',
    automationRate: '70%',
  },
  {
    name: 'Qoyod',
    logo: '/logos/logo-qoyod.png',
    description: '25,000 businesses trust Qoyod for accounting',
    automationRate: '77%',
  },
];

function SwitchedFromFreshdesk() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % switchedCompanies.length);
        setIsAnimating(false);
      }, 400);
    }, 2500);

    return () => clearInterval(interval);
  }, [isPaused]);

  const currentCompany = switchedCompanies[currentIndex];

  return (
    <div className="mt-20">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-col items-start gap-6 md:flex-row md:gap-8">
          {/* Mo's photo */}
          <div className="shrink-0">
            <div className="relative">
              <img
                src="/about/image.png"
                alt={c.moPhotoAlt}
                className="h-20 w-20 rounded-full object-cover ring-4 ring-white shadow-xl md:h-24 md:w-24"
              />
              <div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-black text-xs">
                👋
              </div>
            </div>
          </div>

          {/* Quote */}
          <div className="flex-1">
            <blockquote className="text-lg leading-relaxed text-neutral-700 md:text-xl">
              <span className="font-semibold text-neutral-900">
                {c.quoteIntroA}
              </span>{' '}
              {c.quoteIntroB}{' '}
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="relative mx-2 inline-flex h-6 w-28 cursor-pointer items-center justify-center align-middle md:h-7 md:w-32"
                      style={{
                        verticalAlign: 'middle',
                        transform: 'translateY(-2px)',
                      }}
                      onMouseEnter={() => setIsPaused(true)}
                      onMouseLeave={() => setIsPaused(false)}
                    >
                      <span
                        className={cn(
                          'absolute inset-0 flex items-center justify-center transition-all duration-400',
                          isAnimating
                            ? 'opacity-0 translate-y-2'
                            : 'opacity-100 translate-y-0',
                        )}
                      >
                        <img
                          src={currentCompany.logo}
                          alt={currentCompany.name}
                          className="max-h-5 max-w-full object-contain md:max-h-6"
                        />
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs px-4 py-3">
                    <p className="font-semibold text-white">
                      {currentCompany.name}
                    </p>
                    <p className="mt-1 text-sm text-neutral-300">
                      {(t.logos as Record<string, string>)[
                        currentCompany.description
                      ] || currentCompany.description}
                    </p>
                    {currentCompany.automationRate && (
                      <p className="mt-1.5 text-xs text-green-400">
                        {currentCompany.automationRate} {c.automationWithOpen}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>{' '}
              {c.quoteMid}{' '}
              <span className="font-medium text-neutral-900">{c.quoteEnd}</span>
            </blockquote>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm font-semibold text-neutral-900">
                Mo Gharbat
              </span>
              <span className="text-sm text-neutral-500">{c.role}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type ComparisonStatus = 'best' | 'worst' | 'neutral';

interface ComparisonRow {
  metric: string;
  openAI: { value: string; status: ComparisonStatus };
  freshdesk: { value: string; status: ComparisonStatus };
}

export default function FreshdeskPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonData: ComparisonRow[] = [
    {
      metric: t.helpdesk['Setup Time'],
      openAI: { value: t.helpdesk['Under 1 hour'], status: 'best' },
      freshdesk: { value: t.helpdesk['+3 months'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Sophistication'],
      openAI: { value: t.helpdesk['Advanced multi-model'], status: 'best' },
      freshdesk: { value: t.helpdesk['Basic Freddy AI'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Avg Automation Rate'],
      openAI: { value: '77%', status: 'best' },
      freshdesk: { value: '~40-50%', status: 'worst' },
    },
    {
      metric: t.helpdesk['Multi-modal AI'],
      openAI: {
        value: t.helpdesk['Voice, Vision, Text, Files'],
        status: 'best',
      },
      freshdesk: { value: t.helpdesk['Text only'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Quality Assurance'],
      openAI: { value: t.helpdesk['Built-in automatic QA'], status: 'best' },
      freshdesk: { value: t.helpdesk['Manual QA'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Agentic Workflows'],
      openAI: { value: t.helpdesk['Drag-and-drop builder'], status: 'best' },
      freshdesk: { value: t.helpdesk['Basic automation'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Outbound Sequencing'],
      openAI: {
        value: t.helpdesk['WhatsApp, Phone, Email, SMS'],
        status: 'best',
      },
      freshdesk: { value: t.helpdesk['Not available'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Knowledge Gap Detection'],
      openAI: {
        value: t.helpdesk['Auto-detects & suggests content'],
        status: 'best',
      },
      freshdesk: {
        value: t.helpdesk['Manual identification'],
        status: 'worst',
      },
    },
    {
      metric: t.helpdesk['Complex Query Handling'],
      openAI: { value: t.helpdesk['Excellent'], status: 'best' },
      freshdesk: { value: t.helpdesk['Limited'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Context Understanding'],
      openAI: { value: t.helpdesk['Deep context'], status: 'best' },
      freshdesk: { value: t.helpdesk['Simple patterns'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Pricing Model'],
      openAI: { value: t.helpdesk['Outcome-based'], status: 'best' },
      freshdesk: { value: t.helpdesk['Per-agent + add-ons'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Integration Depth'],
      openAI: { value: t.helpdesk['Full API access'], status: 'best' },
      freshdesk: { value: t.helpdesk['Basic integration'], status: 'worst' },
    },
  ];

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={
          <>
            {c.heroTitleA}{' '}
            <span className="whitespace-nowrap">
              <Image
                src="/helpdicks/freshdesk.png"
                alt="Freshdesk"
                width={48}
                height={48}
                className="mr-1 inline-block h-[0.8em] w-[0.8em] rounded-[0.18em] align-[-0.1em]"
              />
              <span className="text-[#25C16F]">Freshdesk</span>
            </span>{' '}
            {c.heroTitleB}
          </>
        }
        subtitle={
          <>
            {c.heroSubtitleA}
            <br />
            {c.heroSubtitleB}
          </>
        }
        videoSrc="/home/features/tebi.mp4"
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleInbound
                source="Freshdesk"
                sourceLogo="/helpdicks/freshdesk.png"
                title={c.inboundTitle}
                preview={c.inboundPreview}
              />
            ),
          },
          {
            role: 'agent',
            widget: (
              <BubbleAutopilot
                title={c.autopilotTitle}
                steps={[...c.autopilotSteps]}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.helpdesk['Get started in minutes']}
        subtitle={
          t.helpdesk['Simple setup, powerful AI. No complex training required.']
        }
        learnMoreLink="#comparison"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: t.helpdesk['Create a new user in Freshdesk'],
            description:
              t.helpdesk[
                'Our AI will work like any other teammate in your Freshdesk workspace.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/channels/freshdesk/create-the-ai-teammate.mp4',
            imagePosition: 'right',
          },
          {
            title: t.helpdesk['Set the routing rules'],
            description:
              t.helpdesk[
                "Route 100% or 10% of your support — it's really up to you."
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/channels/freshdesk/set-the-routing-rules.mp4',
            imagePosition: 'left',
          },
          {
            title: t.helpdesk['Monitor and iterate'],
            description:
              t.helpdesk[
                'Because your new AI works within Freshdesk, all of your reporting and analytics remain intact. Continuously improve with feedback and training.'
              ],
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '77%',
              label: t.helpdesk['Automation Rate'],
              sublabel: t.helpdesk['Industry-leading performance'],
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              <MagicalText>{t.helpdesk['Our AI']}</MagicalText> {c.vs}{' '}
              {t.helpdesk['Freddy AI']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  "See how Open's AI engine delivers sophisticated AI that goes beyond Freddy's basic capabilities."
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.helpdesk['Feature']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      <MagicalText>{t.helpdesk['Our AI']}</MagicalText>{' '}
                      {t.helpdesk['(Open)']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.helpdesk['Freddy AI']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/30 hover:bg-muted/30 transition-colors"
                    >
                      <TableCell className="text-foreground py-4 font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.openAI.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.openAI.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'openAI' &&
                            'bg-green-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('openAI')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.openAI.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.openAI.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.openAI.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.openAI.value}</span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.freshdesk.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.freshdesk.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'freshdesk' &&
                            'bg-red-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('freshdesk')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.freshdesk.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.freshdesk.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.freshdesk.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.freshdesk.value}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Social Proof - Companies that chose Open over Freddy AI */}
          <SwitchedFromFreshdesk />
        </div>
      </section>

      {/* Automate Freshdesk Manual Work Section */}
      <section className="bg-neutral-50 py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.helpdesk['Automate the manual tasks, not just the answers']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  'In addition to fully resolving support tickets, Open automates the manual tasks that slow down your team—routing, tagging, QA, and more.'
                ]
              }
            </p>
          </div>
          <TemplatesList
            systemFilter="Freshdesk"
            categoryFilter="Customer Support"
            limit={6}
            variant="slider"
          />
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.helpdesk[
            "Find answers to common questions about our Freshdesk AI agent. Can't find what you're looking for? Contact our support team."
          ]
        }
        items={[
          {
            question: t.helpdesk['Why is Open better than Freddy AI?'],
            answer:
              t.helpdesk[
                'Freddy AI is limited to simple, pattern-based responses. Open uses advanced multi-model AI that understands context, handles complex technical queries, and provides nuanced answers that feel human.'
              ],
          },
          {
            question: t.helpdesk['Can Open handle complex customer queries?'],
            answer:
              t.helpdesk[
                'Yes. Unlike Freddy which struggles with anything beyond simple FAQs, Open excels at complex, multi-turn conversations, technical troubleshooting, and nuanced customer issues.'
              ],
          },
          {
            question:
              t.helpdesk['Can I test Open and Freddy AI at the same time?'],
            answer:
              t.helpdesk[
                'Yes! We can set up Open for you in a way where you can run an A/B test and decide for yourself. Try it risk-free for 1 month.'
              ],
          },
          {
            question: t.helpdesk['Can I try Open risk-free?'],
            answer:
              t.helpdesk[
                'Absolutely. You can try Open risk-free for a month with free implementation. Our team will help you get set up and see results before you commit.'
              ],
          },
          {
            question:
              t.helpdesk[
                'How long does it take to set up Open with Freshdesk?'
              ],
            answer:
              t.helpdesk[
                'Open can be set up in under an hour. It connects directly to your Freshdesk instance via API and starts resolving queries on day one using your existing help center content.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Does Open work with Freshdesk email and chat channels?'
              ],
            answer:
              t.helpdesk[
                'Yes. Open works across all Freshdesk channels including email tickets, live chat, phone support, and messaging—providing sophisticated AI support everywhere your customers reach out.'
              ],
          },
          {
            question:
              t.helpdesk['Can I see how Open is performing in Freshdesk?'],
            answer:
              t.helpdesk[
                'Yes. Since Open works as a native Freshdesk agent, all your existing reporting, analytics, and dashboards continue to work. Track AI performance alongside your human agents.'
              ],
          },
          {
            question:
              t.helpdesk['Does Open integrate with my Freshdesk workflows?'],
            answer:
              t.helpdesk[
                'Yes. Open respects your existing Freshdesk automation rules, SLA policies, and escalation workflows. It works within your current setup without requiring reconfiguration.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
